import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, MessageCircle, Globe, Heart, Shield, AlertTriangle, ChevronRight, ExternalLink, ChevronDown } from 'lucide-react';
import { IconButton } from '../components/brand/BrandButton';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';

/**
 * Crisis Resources Page
 * Emergency contacts, hotlines, and immediate grounding techniques
 * Accessible, clear, and non-judgmental
 * Supports Australia (default), US, and UK
 */

const CRISIS_RESOURCES = {
    AU: {
        name: 'Australia',
        flag: '🇦🇺',
        emergency: {
            number: '000',
            label: 'Triple Zero (000)',
            description: 'Call Triple Zero (000) for emergency services or go to your nearest emergency department.'
        },
        hotlines: [
            {
                name: 'Lifeline Australia',
                number: '13 11 14',
                description: 'Free, confidential 24/7 crisis support and suicide prevention',
                type: 'call'
            },
            {
                name: 'Beyond Blue',
                number: '1300 22 4636',
                description: 'Support for anxiety, depression, and mental health',
                type: 'call'
            },
            {
                name: 'Suicide Call Back Service',
                number: '1300 659 467',
                description: 'Free 24/7 professional phone and online counselling',
                type: 'call'
            },
            {
                name: 'Kids Helpline',
                number: '1800 55 1800',
                description: 'Free 24/7 support for young people aged 5-25',
                type: 'call'
            },
            {
                name: 'MensLine Australia',
                number: '1300 78 99 78',
                description: 'Support for men with family and relationship concerns',
                type: 'call'
            },
            {
                name: 'Crisis Text Support',
                number: 'Text 0477 13 11 14',
                description: 'Text-based crisis support via Lifeline',
                type: 'text'
            }
        ]
    },
    US: {
        name: 'United States',
        flag: '🇺🇸',
        emergency: {
            number: '911',
            label: '911',
            description: 'Call 911 for emergency services or go to your nearest emergency room.'
        },
        hotlines: [
            {
                name: '988 Suicide & Crisis Lifeline',
                number: '988',
                description: 'Free, confidential 24/7 crisis support. Call or text.',
                type: 'call'
            },
            {
                name: 'Crisis Text Line',
                number: 'Text HOME to 741741',
                description: 'Free 24/7 text-based crisis support',
                type: 'text'
            },
            {
                name: 'SAMHSA National Helpline',
                number: '1-800-662-4357',
                description: 'Free 24/7 mental health and substance abuse support',
                type: 'call'
            },
            {
                name: 'Veterans Crisis Line',
                number: '1-800-273-8255 (Press 1)',
                description: '24/7 support for veterans and their families',
                type: 'call'
            },
            {
                name: 'Trevor Project (LGBTQ+)',
                number: '1-866-488-7386',
                description: '24/7 crisis support for LGBTQ+ young people',
                type: 'call'
            },
            {
                name: 'NAMI Helpline',
                number: '1-800-950-6264',
                description: 'Mental health support and resources (Mon-Fri, 10am-10pm ET)',
                type: 'call'
            }
        ]
    },
    UK: {
        name: 'United Kingdom',
        flag: '🇬🇧',
        emergency: {
            number: '999',
            label: '999',
            description: 'Call 999 for emergency services or go to your nearest A&E department.'
        },
        hotlines: [
            {
                name: 'Samaritans',
                number: '116 123',
                description: 'Free, confidential 24/7 emotional support',
                type: 'call'
            },
            {
                name: 'SHOUT',
                number: 'Text SHOUT to 85258',
                description: 'Free 24/7 text-based crisis support',
                type: 'text'
            },
            {
                name: 'CALM (Men)',
                number: '0800 58 58 58',
                description: 'Support for men in crisis (5pm-midnight daily)',
                type: 'call'
            },
            {
                name: 'Papyrus (Under 35)',
                number: '0800 068 4141',
                description: 'Suicide prevention for young people under 35',
                type: 'call'
            },
            {
                name: 'Mind Infoline',
                number: '0300 123 3393',
                description: 'Mental health information and support (Mon-Fri, 9am-6pm)',
                type: 'call'
            },
            {
                name: 'Childline (Under 19)',
                number: '0800 1111',
                description: 'Free, confidential support for children and young people',
                type: 'call'
            }
        ]
    }
};

const GROUNDING_TECHNIQUES = [
    {
        name: '5-4-3-2-1 Technique',
        steps: [
            '5 things you can SEE',
            '4 things you can TOUCH',
            '3 things you can HEAR',
            '2 things you can SMELL',
            '1 thing you can TASTE'
        ],
        description: 'Engage your senses to anchor to the present moment'
    },
    {
        name: 'Cold Water Reset',
        steps: [
            'Hold ice cubes in your hands',
            'Splash cold water on your face',
            'Press a cold object to your wrists',
            'Take a cold shower if safe'
        ],
        description: 'Activates your dive reflex and calms your nervous system'
    },
    {
        name: 'Box Breathing',
        steps: [
            'Breathe IN for 4 seconds',
            'HOLD for 4 seconds',
            'Breathe OUT for 4 seconds',
            'HOLD for 4 seconds',
            'Repeat 4 times'
        ],
        description: 'Regulates your autonomic nervous system'
    },
    {
        name: 'Physical Grounding',
        steps: [
            'Press your feet firmly into the floor',
            'Push your palms against a wall',
            'Hold something textured (fabric, stone)',
            'Name where you are right now'
        ],
        description: 'Uses physical sensation to anchor you'
    }
];

const SAFETY_REMINDERS = [
    "This feeling will pass. Feelings are temporary, even the most intense ones.",
    "You don't have to have it all figured out right now.",
    "It's okay to ask for help. That's strength, not weakness.",
    "You've survived difficult moments before. You have that strength in you.",
    "One moment at a time. That's all you need to manage."
];

const CrisisResources = () => {
    const navigate = useNavigate();
    const [selectedCountry, setSelectedCountry] = useState('AU');
    const [showCountrySelector, setShowCountrySelector] = useState(false);
    const [expandedTechnique, setExpandedTechnique] = useState(null);
    const [showGrounding, setShowGrounding] = useState(false);

    const resources = CRISIS_RESOURCES[selectedCountry];
    const countries = Object.entries(CRISIS_RESOURCES).map(([code, data]) => ({
        code,
        name: data.name,
        flag: data.flag
    }));

    return (
        <div className="min-h-screen bg-transparent">
            {/* Header - Glass-morphism */}
            <header className="sticky top-0 z-40 glass-header p-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <IconButton onClick={() => navigate(-1)} data-testid="back-btn" className="text-[#7D8E95] hover:text-[#1D6B6B] hover:bg-[#FFBB98]/40 transition-all duration-200">
                            <ArrowLeft className="w-5 h-5" />
                        </IconButton>
                        <div>
                            <Heading level={3} className="text-[#1D6B6B]">
                                Crisis Support
                            </Heading>
                            <SmallText className="text-[#7D8E95]">
                                You're not alone. Help is available.
                            </SmallText>
                        </div>
                    </div>
                    
                    {/* Country Selector */}
                    <div className="relative">
                        <button
                            onClick={() => setShowCountrySelector(!showCountrySelector)}
                            className="flex items-center gap-2 glass-card px-3 py-2 rounded-full hover:bg-white/80 transition-colors"
                            data-testid="country-selector-btn"
                        >
                            <span className="text-lg">{resources.flag}</span>
                            <span className="text-sm text-[#2A8585] font-medium hidden sm:inline">{resources.name}</span>
                            <ChevronDown className={`w-4 h-4 text-[#7D8E95] transition-transform ${showCountrySelector ? 'rotate-180' : ''}`} />
                        </button>
                        
                        {/* Dropdown */}
                        {showCountrySelector && (
                            <div className="absolute right-0 mt-2 glass-card rounded-xl shadow-soft-lg overflow-hidden z-50 min-w-[180px] animate-in fade-in slide-in-from-top-2 duration-200">
                                {countries.map((country) => (
                                    <button
                                        key={country.code}
                                        onClick={() => {
                                            setSelectedCountry(country.code);
                                            setShowCountrySelector(false);
                                        }}
                                        className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#FFF3E0] transition-colors ${
                                            selectedCountry === country.code ? 'bg-[#FFF3E0]' : ''
                                        }`}
                                        data-testid={`country-option-${country.code}`}
                                    >
                                        <span className="text-xl">{country.flag}</span>
                                        <span className="text-sm text-[#1D6B6B] font-medium">{country.name}</span>
                                        {selectedCountry === country.code && (
                                            <span className="ml-auto text-[#1D6B6B]">✓</span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </header>

            <main className="max-w-md mx-auto px-4 py-6 space-y-6">
                {/* Safety Reminder - First, gentle entry */}
                <div className="bg-white/70 rounded-2xl p-5 border border-[#A5D6A7]">
                    <div className="flex items-start gap-3">
                        <Heart className="w-5 h-5 text-[#1D6B6B] flex-shrink-0 mt-1" />
                        <div>
                            <SmallText className="text-[#1B5E20] font-medium block mb-2">
                                Remember
                            </SmallText>
                            <BodyText className="text-[#1D6B6B] italic">
                                {SAFETY_REMINDERS[Math.floor(Math.random() * SAFETY_REMINDERS.length)]}
                            </BodyText>
                        </div>
                    </div>
                </div>

                {/* Quick Grounding Toggle */}
                <button
                    onClick={() => setShowGrounding(!showGrounding)}
                    className="w-full bg-white rounded-2xl p-5 border border-[#FFCCBC] text-left transition-all hover:shadow-md"
                    data-testid="grounding-toggle"
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#FFBB98] flex items-center justify-center">
                                <Shield className="w-5 h-5 text-[#1D6B6B]" />
                            </div>
                            <div>
                                <BodyText className="text-[#1D6B6B] font-semibold">
                                    I need to ground myself right now
                                </BodyText>
                                <SmallText className="text-[#7D8E95]">
                                    Quick techniques to calm your body
                                </SmallText>
                            </div>
                        </div>
                        <ChevronRight className={`w-5 h-5 text-[#7D8E95] transition-transform ${showGrounding ? 'rotate-90' : ''}`} />
                    </div>
                </button>

                {/* Grounding Techniques (Expandable) */}
                {showGrounding && (
                    <div className="space-y-3 animate-in fade-in slide-in-from-top-2 duration-300">
                        {GROUNDING_TECHNIQUES.map((technique, index) => (
                            <div 
                                key={index}
                                className="bg-white rounded-xl border border-white/40 overflow-hidden"
                            >
                                <button
                                    onClick={() => setExpandedTechnique(expandedTechnique === index ? null : index)}
                                    className="w-full p-4 text-left flex items-center justify-between"
                                >
                                    <div>
                                        <BodyText className="text-[#1D6B6B] font-medium">
                                            {technique.name}
                                        </BodyText>
                                        <SmallText className="text-[#7D8E95]">
                                            {technique.description}
                                        </SmallText>
                                    </div>
                                    <ChevronRight className={`w-5 h-5 text-[#7D8E95] transition-transform ${expandedTechnique === index ? 'rotate-90' : ''}`} />
                                </button>
                                
                                {expandedTechnique === index && (
                                    <div className="px-4 pb-4 pt-0 border-t border-white/40">
                                        <ul className="space-y-2 mt-3">
                                            {technique.steps.map((step, stepIndex) => (
                                                <li key={stepIndex} className="flex items-start gap-2">
                                                    <span className="w-5 h-5 rounded-full bg-white/70 text-[#1D6B6B] text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                                                        {stepIndex + 1}
                                                    </span>
                                                    <SmallText className="text-[#2A8585]">{step}</SmallText>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Crisis Hotlines */}
                <div>
                    <div className="flex items-center gap-2 mb-4">
                        <Heading level={3} className="text-[#1D6B6B]">
                            Crisis Hotlines
                        </Heading>
                        <span className="text-lg">{resources.flag}</span>
                    </div>
                    <div className="space-y-3">
                        {resources.hotlines.map((hotline, index) => (
                            <div 
                                key={index}
                                className="bg-white rounded-xl p-4 border border-white/40"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1">
                                        <BodyText className="text-[#1D6B6B] font-semibold mb-1">
                                            {hotline.name}
                                        </BodyText>
                                        <SmallText className="text-[#7D8E95] block">
                                            {hotline.description}
                                        </SmallText>
                                    </div>
                                    <div className="flex-shrink-0">
                                        {hotline.type === 'call' && (
                                            <a 
                                                href={`tel:${hotline.number.replace(/\D/g, '')}`}
                                                className="inline-flex items-center gap-2 bg-[#E3F2FD] text-[#1565C0] px-3 py-2 rounded-full text-sm font-medium"
                                            >
                                                <Phone className="w-4 h-4" />
                                                <span className="text-xs sm:text-sm">{hotline.number}</span>
                                            </a>
                                        )}
                                        {hotline.type === 'text' && (
                                            <div className="inline-flex items-center gap-2 bg-white/70 text-[#1D6B6B] px-3 py-2 rounded-full text-sm font-medium">
                                                <MessageCircle className="w-4 h-4" />
                                                <span className="text-xs">{hotline.number}</span>
                                            </div>
                                        )}
                                        {hotline.type === 'web' && (
                                            <a 
                                                href={hotline.number}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-2 bg-[#F3E5F5] text-[#7B1FA2] px-3 py-2 rounded-full text-sm font-medium"
                                            >
                                                <Globe className="w-4 h-4" />
                                                <ExternalLink className="w-3 h-3" />
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Emergency Contact - Subtle placement */}
                <div className="bg-white rounded-xl p-4 border border-white/40">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <AlertTriangle className="w-5 h-5 text-[#BF360C]" />
                            <div>
                                <SmallText className="text-[#2A8585] font-medium">
                                    Immediate danger?
                                </SmallText>
                                <SmallText className="text-[#7D8E95]">
                                    {resources.emergency.description.split('.')[0]}
                                </SmallText>
                            </div>
                        </div>
                        <a 
                            href={`tel:${resources.emergency.number.replace(/\D/g, '')}`}
                            className="inline-flex items-center gap-2 bg-[#FFEBE5] text-[#BF360C] px-3 py-2 rounded-full text-sm font-medium"
                            data-testid="emergency-call-btn"
                        >
                            <Phone className="w-4 h-4" />
                            {resources.emergency.label}
                        </a>
                    </div>
                </div>

                {/* Additional Resources */}
                <div className="bg-[#F5F5F5] rounded-2xl p-5">
                    <Heading level={4} className="text-[#1D6B6B] mb-3">
                        You might also find helpful
                    </Heading>
                    <div className="space-y-2">
                        <button 
                            onClick={() => navigate('/regulate/still-sphere')}
                            className="w-full text-left p-3 bg-white rounded-xl flex items-center justify-between hover:bg-[#FAFAFA] transition-colors"
                        >
                            <SmallText className="text-[#2A8585]">Still Sphere — for overwhelm</SmallText>
                            <ChevronRight className="w-4 h-4 text-[#7D8E95]" />
                        </button>
                        <button 
                            onClick={() => navigate('/regulate/body-scan')}
                            className="w-full text-left p-3 bg-white rounded-xl flex items-center justify-between hover:bg-[#FAFAFA] transition-colors"
                        >
                            <SmallText className="text-[#2A8585]">Body Scan — reconnect with your body</SmallText>
                            <ChevronRight className="w-4 h-4 text-[#7D8E95]" />
                        </button>
                        <button 
                            onClick={() => navigate('/regulate/breathing-orb')}
                            className="w-full text-left p-3 bg-white rounded-xl flex items-center justify-between hover:bg-[#FAFAFA] transition-colors"
                        >
                            <SmallText className="text-[#2A8585]">Calm Cycle — slow your breathing</SmallText>
                            <ChevronRight className="w-4 h-4 text-[#7D8E95]" />
                        </button>
                    </div>
                </div>

                {/* Bottom padding */}
                <div className="h-8" />
            </main>
        </div>
    );
};

export default CrisisResources;
